import { getPlatformType, getSellingCountry, getCountryCodeFromLocationData, convertDateToTimestamp } from '@/utils/homepage/visaDenial'
import { getPartnerId } from '@/utils/cfw'
import { VisaDenialPayload } from '@/utils/homepage/visaDenialApi'

/**
 * Interface for visa denial eligibility parameters
 */
export interface VisaDenialParams {
    // Required fields
    source: string // Airport code (e.g., 'DXB')
    destination: string // Airport code (e.g., 'BLR')
    departDate: string // Date in DD/MM/YYYY format
    tripType: 'ONEWAY' | 'ROUNDTRIP' | 'O' | 'R' // Supports both formats
    isIntl: boolean | string // true/false or 'y'/'n'
    
    // Optional fields
    returnDate?: string // Date in DD/MM/YYYY format (required for roundtrip)
    isCfw?: boolean // Whether CFW flow is active
    
    // Country code extraction sources (optional)
    airportSearchBlukResponse?: any[] // Response from airportSearchBluk API
    metaData?: any // Metadata from SRP (for fallback country code extraction)
}

/**
 * Extract country code from airportSearchBluk response
 * @param airportSearchBlukResponse - Response array from airportSearchBluk API
 * @param sourceAirportCode - Source airport code to find in the response
 * @returns Country code (e.g., 'IN', 'AE') or undefined
 */
export const extractCountryCodeFromAirportSearchBluk = (
    airportSearchBlukResponse: any[],
    sourceAirportCode: string
): string | undefined => {
    if (!airportSearchBlukResponse || !Array.isArray(airportSearchBlukResponse) || !sourceAirportCode) {
        return undefined
    }

    try {
        const departureAirport = airportSearchBlukResponse.find(
            (airport: any) => airport.airportCode === sourceAirportCode
        )
        return departureAirport?.countryCode
    } catch (e) {
        console.error('Error extracting country code from airportSearchBluk response:', e)
        return undefined
    }
}

/**
 * Determine whether a sector is international using the authoritative airport
 * country codes from the airportSearchBluk response.
 *
 * The URL/route-derived intl flag (e.g. processedData.isIntl) goes stale when the
 * user changes the to/from airports on SRP, so it must not be trusted on its own.
 * When both country codes are resolvable we compare them directly; we only fall
 * back to the route flag when the country codes are unavailable.
 *
 * @returns true = international sector, false = domestic sector
 */
export const isInternationalSector = (
    airportSearchBlukResponse: any[] | undefined,
    source: string,
    destination: string,
    routeIsIntl: boolean
): boolean => {
    const sourceCountryCode = extractCountryCodeFromAirportSearchBluk(airportSearchBlukResponse || [], source)
    const destinationCountryCode = extractCountryCodeFromAirportSearchBluk(airportSearchBlukResponse || [], destination)

    if (sourceCountryCode && destinationCountryCode) {
        return sourceCountryCode !== destinationCountryCode
    }

    // Country codes unavailable — fall back to the route flag as a best effort.
    return routeIsIntl
}

/**
 * Create Visa Denial Eligibility payload
 * This function handles country code extraction and payload creation
 * 
 * @param params - Visa denial eligibility parameters
 * @returns VisaDenialPayload ready to be dispatched
 */
export const createVisaDenialPayload = (params: VisaDenialParams): VisaDenialPayload | null => {
    const {
        source,
        destination,
        departDate,
        returnDate,
        tripType,
        isIntl,
        isCfw = false,
        airportSearchBlukResponse,
        metaData
    } = params

    // Validate required fields
    if (!source || !destination || !departDate) {
        console.warn('Missing required fields for visa denial payload:', { source, destination, departDate })
        return null
    }

    // Validate roundtrip has return date
    const normalizedTripType = tripType === 'R' || tripType === 'ROUNDTRIP' ? 'ROUNDTRIP' : 'ONEWAY'
    if (normalizedTripType === 'ROUNDTRIP' && !returnDate) {
        console.warn('Roundtrip requires returnDate')
        return null
    }

    // Extract country code with priority:
    // 1. airportSearchBlukResponse (if available)
    // 2. metaData (fallback for SRP)
    // 3. Default to 'IN'
    let finalCountryCode = 'IN' // Default fallback

    if (airportSearchBlukResponse) {
        const countryCode = extractCountryCodeFromAirportSearchBluk(airportSearchBlukResponse, source)
        if (countryCode) {
            finalCountryCode = countryCode
        }
    }

    // Fallback to metaData if airportSearchBlukResponse didn't provide country code
    if (finalCountryCode === 'IN' && metaData) {
        const countryCodeFromMeta = getCountryCodeFromLocationData(metaData?.airportDetail, source)
        if (countryCodeFromMeta) {
            finalCountryCode = countryCodeFromMeta
        }
    }

    // Determine sector type
    const isInternational = isIntl === true || isIntl === 'y' || isIntl === 'Y'
    const sectorType = isInternational ? 'INTERNATIONAL' : 'DOMESTIC'

    // Get selling country and platform type
    const sellingCountry = getSellingCountry()
    const platformType = getPlatformType()

    // Convert dates to timestamps
    const departDateTimestamp = convertDateToTimestamp(departDate)
    const returnDateTimestamp = returnDate ? convertDateToTimestamp(returnDate) : undefined

    // Create payload
    const payload: VisaDenialPayload = {
        travelCategory: 'FLIGHT',
        travelContext: {
            demandContext: {
                partnerId: getPartnerId(isCfw).toString(),
                platformType,
                sellingCountry,
                userCohort: '',
            },
        },
        tripDetail: {
            journey: {
                departureAirport: source,
                arrivalAirport: destination,
                countryCode: finalCountryCode,
                departDate: departDateTimestamp,
                ...(normalizedTripType === 'ROUNDTRIP' && returnDateTimestamp
                    ? { returnDate: returnDateTimestamp }
                    : {}),
            },
            sectorType,
            tripType: normalizedTripType,
        },
    }

    return payload
}

